import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import { useUpdateScreenChatContext } from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { getId } from '$tablet-app/src/app/models';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import useShoppingList from '$tablet-app/src/app/recipes/useShoppingList';
import { useServingsVariable } from '$tablet-app/src/app/state_variables';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { toggleInList } from '$tablet-app/src/app/utils/arrays';
import { TaskListType } from '@akin-home/amplify';
import { useGetMeal, useSwapMealRecipe } from '@akin-home/meals/react';
import { RecipeIngredient, getDefaultRecipeServings } from '@akin-home/recipes';
import { useGetRecipe } from '@akin-home/recipes/react';
import { showInfoToast, showSuccessToast } from '@akin-home/toast/native';
import { multiplyString } from '@akin-home/utils/strings';
import React, { useEffect, useState } from 'react';
import { useToggleRecipeFavourite } from '../../hooks/useToggleRecipeFavourite';
import RecipeDetailsView from './RecipeDetailsView';

/** State provider for Recipe Details View */
const RecipeDetailsScreen = (props: RootStackScreenProps<'RecipeDetails'>) => {
  const {
    navigation: { navigate, goBack },
    route,
  } = props;

  const { recipe_id, meal_id } = route.params;

  const { data: recipe, isLoading: recipeLoading } = useGetRecipe({
    id: recipe_id,
  });
  const favourite_recipe_ids = useStateStore(
    (state) => state.current_user?.favourite_recipe_ids
  );

  const toggleRecipeFavourite = useToggleRecipeFavourite();
  const swap_recipe = useSwapMealRecipe();

  const { item: meal } = useGetMeal({ id: meal_id }, { enabled: !!meal_id });

  const { servings: usual_servings, isLoading } = useServingsVariable();

  const [servings, setServings] = useState<number>();

  useUpdateScreenChatContext();

  const {
    shoppingList,
    addItem,
    removeItem,
    removeItems,
    addItems,
    isSuccess,
  } = useShoppingList();

  const [selected_ingredient_ids, setSelectedIngredientIDs] = useState<
    string[]
  >([]);

  const [hasFilledIngredientList, setHasFilledIngredientList] = useState(false);

  const isFavourite = favourite_recipe_ids?.includes(recipe_id);

  const onPressFavourite = () => {
    recipe && toggleRecipeFavourite(recipe);
  };

  const onPressSwap =
    meal && recipe
      ? async () => {
          showInfoToast({ text1: 'Swapping Meal...' });
          navigate('Main', { screen: 'Meals' });
          await swap_recipe.mutateAsync({ meal, recipe });
          showSuccessToast({ text1: 'Swapped Meal' });
        }
      : undefined;

  const onPressAddAllIngredients = () => {
    const ingredients = recipe?.ingredients || [];

    setSelectedIngredientIDs(ingredients.map(getId) || []);

    if (shoppingList) {
      const items = ingredients.filter(
        (i) => !shoppingList.checklist.some((l) => l.title.includes(i.name))
      );

      if (items.length) {
        addItems(
          items.map((ingredient) => ({
            recipe_id: recipe?.id as string,
            name: ingredient.name,
            unit: ingredient.unitOfMeasure,
            quantity: multiplyString(ingredient.quantity, servings || 1).value,
          }))
        );
      }
    }
  };

  const onPressRemoveAllIngredients = () => {
    const ingredient_names = recipe?.ingredients.map((i) => i.name) || [];

    setSelectedIngredientIDs([]);

    if (shoppingList) {
      const items = shoppingList.checklist
        .filter((l) => ingredient_names.includes(l.title))
        .map(getId);

      if (items.length) {
        removeItems(items, recipe?.id as string);
      }
    }
  };

  const onToggleSelectedIngredient = (ingredient: RecipeIngredient) => {
    setSelectedIngredientIDs(
      toggleInList(selected_ingredient_ids, ingredient.id)
    );

    if (shoppingList) {
      const existing_item = shoppingList.checklist.find((l) =>
        l.title.includes(ingredient.name)
      );

      if (existing_item) {
        removeItem(existing_item.id, recipe?.id as string);
      } else {
        addItem({
          recipe_id: recipe?.id as string,
          name: ingredient.name,
          unit: ingredient.unitOfMeasure,
          quantity: multiplyString(ingredient.quantity, servings || 1).value,
        });
      }
    }
  };

  const onPressCreateShoppingList =
    isSuccess && !shoppingList
      ? () =>
          navigate('CreateChecklist', {
            defaults: {
              title: 'Shopping List',
              type: TaskListType.Shopping,
              primaryShoppingList: true,
              emoji: '🛒',
            },
          })
      : undefined;

  useEffect(() => {
    if (recipe && shoppingList && !hasFilledIngredientList) {
      const checklist_labels = shoppingList.checklist.map((l) => l.title);
      setSelectedIngredientIDs(
        recipe.ingredients
          .filter((i) => checklist_labels.includes(i.name))
          .map(getId)
      );
      setHasFilledIngredientList(true);
    }
  }, [recipe, shoppingList, hasFilledIngredientList]);

  /** When the recipe and override serving variable has loaded, store the state locally */
  useEffect(() => {
    if (!isLoading && !recipeLoading && recipe && !servings) {
      setServings(getDefaultRecipeServings(recipe, usual_servings));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, recipeLoading]);

  useEffect(() => {
    if (!recipeLoading && recipe) {
      sendBackgroundMessage(intent_labels.open_recipe, {
        context: {
          recipe: {
            id: recipe.id,
            name: recipe.name,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeLoading]);

  if (recipeLoading) {
    return <FullScreenLoader />;
  }

  if (recipe) {
    return (
      <RecipeDetailsView
        recipe={recipe}
        enable_add_to_shopping_list={!!shoppingList}
        servings={servings}
        selected_ingredient_ids={selected_ingredient_ids}
        isFavourite={isFavourite}
        onChangeServings={setServings}
        onPressCreateShoppingList={onPressCreateShoppingList}
        onPressBack={goBack}
        onPressFavourite={onPressFavourite}
        onPressSwap={onPressSwap}
        onPressAddAllIngredients={onPressAddAllIngredients}
        onPressRemoveAllIngredients={onPressRemoveAllIngredients}
        onToggleSelectedIngredient={onToggleSelectedIngredient}
      />
    );
  }

  return null;
};

export default RecipeDetailsScreen;
